import get from 'lodash/get'

export const PREVIEW_SIZE_PRESETS = [
  { id: 'widescreen', label: 'Widescreen', width: 1920, height: 1080 },
  { id: 'article', label: 'Article', width: 1200, height: 675 },
  { id: 'linkedin', label: 'LinkedIn', width: 1200, height: 627 },
  { id: 'twitter', label: 'Twitter / X', width: 1600, height: 900 },
  { id: 'square', label: 'Square', width: 1080, height: 1080 },
  { id: 'story', label: 'Story', width: 1080, height: 1920 },
]

const PRESET_DIMENSIONS = {
  widescreen: { width: 1920, height: 1080 },
  article: { width: 1200, height: 675 },
  linkedin: { width: 1200, height: 627 },
  twitter: { width: 1600, height: 900 },
  square: { width: 1080, height: 1080 },
  story: { width: 1080, height: 1920 },
  standard: { width: 1920, height: 1080 },
}

function getMappedColumns(mapping, dimensionIds) {
  return dimensionIds
    .flatMap((dimensionId) => get(mapping, `[${dimensionId}].value`, []))
    .filter(Boolean)
}

function getUniqueValues(dataset, column) {
  return Array.from(
    new Set((dataset || []).map((row) => row?.[column]).filter((value) => value !== undefined && value !== null))
  )
}

function getLongestLabelLength(values) {
  return values.reduce((max, value) => {
    const next = String(value ?? '').trim().length
    return next > max ? next : max
  }, 0)
}

function getDataTypeName(typeDef) {
  if (typeof typeDef === 'string') return typeDef
  if (typeDef && typeof typeDef === 'object') {
    return typeDef.type || typeDef.name || ''
  }
  return ''
}

function getNumericTickWidth(dataset, dataTypes, columns) {
  const numericColumns = columns.filter(
    (column) => getDataTypeName(dataTypes?.[column]) === 'number'
  )

  const longestNumericLabel = numericColumns.reduce((max, column) => {
    const width = (dataset || []).reduce((columnMax, row) => {
      const value = row?.[column]
      if (value === undefined || value === null || value === '') {
        return columnMax
      }
      return Math.max(columnMax, String(value).length)
    }, 0)

    return Math.max(max, width)
  }, 0)

  if (longestNumericLabel >= 9) return 120
  if (longestNumericLabel >= 6) return 80
  if (longestNumericLabel >= 4) return 48
  return 0
}

export function getPreviewSizeDimensions({
  chart,
  dataset,
  mapping,
  dataTypes,
  preset = 'auto',
  customDimensions,
}) {
  if (preset === 'custom') {
    const customWidth = Number.parseInt(customDimensions?.width, 10)
    const customHeight = Number.parseInt(customDimensions?.height, 10)

    if (customWidth > 0 && customHeight > 0) {
      return {
        width: customWidth,
        height: customHeight,
      }
    }
  }

  if (preset !== 'auto') {
    return PRESET_DIMENSIONS[preset] || PRESET_DIMENSIONS.standard
  }

  const chartId = chart?.metadata?.id || ''
  const labelColumns = getMappedColumns(mapping, ['bars', 'x', 'labels', 'hierarchy'])
  const metricColumns = getMappedColumns(mapping, ['y', 'size', 'x'])
  const primaryLabelColumn = labelColumns[0]
  const labelValues = primaryLabelColumn ? getUniqueValues(dataset, primaryLabelColumn) : []
  const categoryCount = labelValues.length
  const longestLabel = getLongestLabelLength(labelValues)
  const numericWidthAllowance = getNumericTickWidth(dataset, dataTypes, metricColumns)

  let width = 680
  let height = 420

  if (chartId.includes('line')) {
    width = 760
    height = 430
  } else if (chartId.includes('pointmap')) {
    width = 840
    height = 480
  } else if (chartId.includes('bubble') || chartId.includes('scatter')) {
    width = 620
    height = 480
  } else if (chartId.includes('treemap')) {
    width = 620
    height = 540
  } else if (chartId.includes('bar')) {
    width = 640
    height = 420
  }

  if (categoryCount >= 10) {
    width += 80
    height += 40
  } else if (categoryCount >= 6) {
    width += 40
  }

  if (longestLabel >= 20) {
    width += 160
    height += 40
  } else if (longestLabel >= 12) {
    width += 90
  }

  width += numericWidthAllowance

  if (chartId.includes('bar') && categoryCount >= 8) {
    height = Math.min(720, 360 + categoryCount * 28)
  }

  if (chartId.includes('line') && categoryCount >= 12) {
    width += 100
  }

  return {
    width: Math.max(520, Math.round(width)),
    height: Math.max(340, Math.round(height)),
  }
}
